import { api } from "../api";
import { openModal, closeModal, modalBody } from "../components/modal";
import type { Department, DepartmentGroup } from "../types";
import { esc } from "../utils/html";

let departments: Department[] = [];
let groups: DepartmentGroup[] = [];
let currentEl: HTMLElement | null = null;

async function loadDepartments() {
  departments = await api.admin.departments.list();
}

async function loadGroups() {
  groups = await api.admin.departmentGroups.list();
}

// ══════════════════════════════════════════════════════════════
// FACHBEREICHE
// ══════════════════════════════════════════════════════════════

function renderDepartmentsTable(el: HTMLElement) {
  const tbody = el.querySelector("#dept-tbody")!;
  if (departments.length === 0) {
    tbody.innerHTML = `<tr><td colspan="6" class="empty-state">Keine Fachbereiche vorhanden</td></tr>`;
    return;
  }
  tbody.innerHTML = departments.map(d => `
    <tr data-id="${d.id}">
      <td><span class="dept-tag" style="background:${d.color}">&nbsp;</span></td>
      <td style="font-weight:500">${esc(d.nameDe)}</td>
      <td class="text-muted">${esc(d.shortName)}</td>
      <td class="text-muted text-center">${d.requiredHours || "–"}</td>
      <td class="text-muted text-center">${d.sortOrder}</td>
      <td style="white-space:nowrap">
        <button class="btn btn-sm btn-secondary btn-edit-dept" data-id="${d.id}">Bearbeiten</button>
        <button class="btn btn-sm btn-danger btn-del-dept" data-id="${d.id}">Löschen</button>
      </td>
    </tr>`).join("");

  tbody.querySelectorAll(".btn-edit-dept").forEach(btn => btn.addEventListener("click", (e) => {
    e.stopPropagation();
    const id = (btn as HTMLElement).dataset.id!;
    const dept = departments.find(d => d.id == id);
    if (dept) openDeptForm(dept);
  }));

  tbody.querySelectorAll(".btn-del-dept").forEach(btn => btn.addEventListener("click", async (e) => {
    e.stopPropagation();
    const id = (btn as HTMLElement).dataset.id!;
    const dept = departments.find(d => d.id == id);
    if (!confirm(`Fachbereich "${dept?.nameDe}" wirklich löschen?`)) return;
    try {
      await api.admin.departments.delete(id);
      await loadDepartments();
      renderDepartmentsTable(el);
    } catch (err: any) { alert(err.message); }
  }));
}

function openDeptForm(dept?: Department) {
  const isEdit = !!dept;
  openModal(isEdit ? "Fachbereich bearbeiten" : "Neuer Fachbereich", `
    <div class="form-grid">
      <div class="form-group col-span-2"><label>Name *</label><input id="f-name" value="${esc(dept?.nameDe || "")}" placeholder="z.B. Chirurgie" /></div>
      <div class="form-group"><label>Kurzname *</label><input id="f-short" value="${esc(dept?.shortName || "")}" placeholder="z.B. Chir" maxlength="10" /></div>
      <div class="form-group"><label>Farbe *</label><input type="color" id="f-color" value="${dept?.color || "#3b82f6"}" style="width:100%;height:38px;padding:2px;cursor:pointer" /></div>
      <div class="form-group"><label>Pflichtstunden <span style="font-weight:400;color:var(--gray-500);font-size:.8125rem">(Schulvorgabe/Ziel)</span></label><input type="number" id="f-hours" value="${dept?.requiredHours || 0}" min="0" /></div>
      <div class="form-group"><label>Sortierung</label><input type="number" id="f-sort" value="${dept?.sortOrder || 0}" min="0" /></div>
    </div>
    <div class="form-actions"><button class="btn btn-secondary" id="f-cancel">Abbrechen</button><button class="btn btn-primary" id="f-save">${isEdit ? "Speichern" : "Erstellen"}</button></div>`);

  const mb = modalBody();
  mb.querySelector("#f-cancel")!.addEventListener("click", closeModal);
  mb.querySelector("#f-save")!.addEventListener("click", async () => {
    const data: any = {
      nameDe: (mb.querySelector("#f-name") as HTMLInputElement).value.trim(),
      shortName: (mb.querySelector("#f-short") as HTMLInputElement).value.trim(),
      color: (mb.querySelector("#f-color") as HTMLInputElement).value,
      requiredHours: parseInt((mb.querySelector("#f-hours") as HTMLInputElement).value) || 0,
      sortOrder: parseInt((mb.querySelector("#f-sort") as HTMLInputElement).value) || 0,
    };
    if (!data.nameDe || !data.shortName) { alert("Name und Kurzname sind erforderlich"); return; }
    try {
      if (isEdit) await api.admin.departments.update(dept!.id, data);
      else await api.admin.departments.create(data);
      closeModal();
      await loadDepartments();
      if (currentEl) renderDepartmentsTable(currentEl);
    } catch (err: any) { alert(err.message); }
  });
  (mb.querySelector("#f-name") as HTMLInputElement).focus();
}

// ══════════════════════════════════════════════════════════════
// FACHBEREICH-GRUPPEN
// ══════════════════════════════════════════════════════════════

function renderGroupsTable(el: HTMLElement) {
  const tbody = el.querySelector("#group-tbody")!;
  if (groups.length === 0) {
    tbody.innerHTML = `<tr><td colspan="6" class="empty-state">Keine Gruppen vorhanden</td></tr>`;
    return;
  }
  tbody.innerHTML = groups.map(g => {
    const tags = g.departments.map(d =>
      `<span class="dept-tag" style="background:${d.color}">${esc(d.shortName)}</span>`
    ).join(" ");
    const poolLabel = g.poolHours != null && g.poolHours > 0
      ? `<span class="badge badge-blue">${g.poolHours}h z.V.</span>`
      : '<span class="text-muted">–</span>';
    return `<tr data-id="${g.id}">
      <td style="font-weight:500">${esc(g.name)}</td>
      <td>${tags || "–"}</td>
      <td class="text-center">${g.isPublic ? '<span class="badge badge-green">Ja</span>' : '<span class="badge badge-gray">Nein</span>'}</td>
      <td class="text-center">${poolLabel}</td>
      <td class="text-muted text-center">${g.schoolCount}</td>
      <td style="white-space:nowrap">
        <button class="btn btn-sm btn-secondary btn-edit-group" data-id="${g.id}">Bearbeiten</button>
        <button class="btn btn-sm btn-danger btn-del-group" data-id="${g.id}">Löschen</button>
      </td>
    </tr>`;
  }).join("");

  tbody.querySelectorAll(".btn-edit-group").forEach(btn => btn.addEventListener("click", (e) => {
    e.stopPropagation();
    const id = (btn as HTMLElement).dataset.id!;
    const group = groups.find(g => g.id == parseInt(id));
    if (group) openGroupForm(group);
  }));

  tbody.querySelectorAll(".btn-del-group").forEach(btn => btn.addEventListener("click", async (e) => {
    e.stopPropagation();
    const id = (btn as HTMLElement).dataset.id!;
    const group = groups.find(g => g.id == parseInt(id));
    if (!confirm(`Gruppe "${group?.name}" wirklich löschen?`)) return;
    try {
      await api.admin.departmentGroups.delete(parseInt(id));
      await loadGroups();
      renderGroupsTable(el);
    } catch (err: any) { alert(err.message); }
  }));
}

function openGroupForm(group?: DepartmentGroup) {
  const isEdit = !!group;
  const selectedIds = group?.departments.map(d => d.id) || [];
  const hasPool = group?.poolHours != null && group.poolHours > 0;
  const poolHoursVal = group?.poolHours ?? 0;

  const checkboxes = departments.map(d => {
    const checked = selectedIds.includes(d.id) ? "checked" : "";
    return `<label style="display:flex;align-items:center;gap:.5rem;padding:.375rem 0;cursor:pointer">
      <input type="checkbox" class="f-dept-cb" value="${d.id}" ${checked} style="width:16px;height:16px;accent-color:${d.color}" />
      <span class="dept-tag" style="background:${d.color}">${esc(d.shortName)}</span>
      <span>${esc(d.nameDe)}</span>
    </label>`;
  }).join("");

  openModal(isEdit ? "Gruppe bearbeiten" : "Neue Gruppe", `
    <div class="form-group"><label>Gruppenname *</label><input id="f-gname" value="${esc(group?.name || "")}" placeholder="z.B. Therapiestandard" /></div>
    <div class="form-group" style="margin-top:.75rem">
      <label style="display:flex;align-items:center;gap:.5rem;cursor:pointer">
        <input type="checkbox" id="f-public" ${group?.isPublic ? "checked" : ""} style="width:16px;height:16px" />
        Öffentlich <span class="text-muted" style="font-weight:400;font-size:.8125rem">(Schulen können diese Gruppe in ihren Einstellungen einschalten. Bei bestehenden Schulen ist sie zunächst ausgeschaltet.)</span>
      </label>
    </div>
    <div class="form-group" style="margin-top:.75rem">
      <label style="display:flex;align-items:center;gap:.5rem;cursor:pointer">
        <input type="checkbox" id="f-has-pool" ${hasPool ? "checked" : ""} style="width:16px;height:16px" />
        Stunden zur Verteilung <span class="text-muted" style="font-weight:400;font-size:.8125rem">(Schulen können diese Stunden auf Fachbereiche aufteilen)</span>
      </label>
      <div id="f-pool-section" style="margin-top:.5rem;padding-left:1.5rem;${hasPool ? "" : "display:none"}">
        <label style="font-size:.875rem">Zu verteilende Stunden gesamt</label>
        <input type="number" id="f-pool-hours" value="${poolHoursVal}" min="0" style="max-width:140px" placeholder="z.B. 500" />
      </div>
    </div>
    <fieldset style="margin-top:.75rem"><legend>Fachbereiche in dieser Gruppe</legend>
      <div style="max-height:300px;overflow-y:auto">${checkboxes || "<p class=\"text-muted\">Keine Fachbereiche vorhanden. Bitte zuerst Fachbereiche anlegen.</p>"}</div>
    </fieldset>
    <div class="form-actions"><button class="btn btn-secondary" id="f-cancel">Abbrechen</button><button class="btn btn-primary" id="f-save">${isEdit ? "Speichern" : "Erstellen"}</button></div>`);

  const mb = modalBody();
  mb.querySelector("#f-cancel")!.addEventListener("click", closeModal);
  mb.querySelector("#f-has-pool")!.addEventListener("change", (e) => {
    const checked = (e.target as HTMLInputElement).checked;
    (mb.querySelector("#f-pool-section") as HTMLElement).style.display = checked ? "" : "none";
  });
  mb.querySelector("#f-save")!.addEventListener("click", async () => {
    const name = (mb.querySelector("#f-gname") as HTMLInputElement).value.trim();
    if (!name) { alert("Gruppenname ist erforderlich"); return; }
    const isPublic = (mb.querySelector("#f-public") as HTMLInputElement).checked;
    const hasPoolChecked = (mb.querySelector("#f-has-pool") as HTMLInputElement).checked;
    const poolHours = hasPoolChecked ? (parseInt((mb.querySelector("#f-pool-hours") as HTMLInputElement).value) || 0) : null;
    const departmentIds = Array.from(mb.querySelectorAll(".f-dept-cb:checked")).map(cb => (cb as HTMLInputElement).value);
    try {
      if (isEdit) await api.admin.departmentGroups.update(group!.id, { name, departmentIds, isPublic, poolHours });
      else await api.admin.departmentGroups.create({ name, departmentIds, isPublic, poolHours });
      closeModal();
      await loadGroups();
      if (currentEl) renderGroupsTable(currentEl);
    } catch (err: any) { alert(err.message); }
  });
  (mb.querySelector("#f-gname") as HTMLInputElement).focus();
}

// ══════════════════════════════════════════════════════════════
// RENDER
// ══════════════════════════════════════════════════════════════

export async function render(el: HTMLElement) {
  currentEl = el;
  el.innerHTML = `
    <div class="page-header">
      <div>
        <h1>Fachbereiche & Gruppen</h1>
        <div style="font-size:.875rem;color:var(--gray-500);margin-top:.25rem">Fachbereiche verwalten und in Gruppen für Mandanten zusammenfassen</div>
      </div>
    </div>

    <div style="display:grid;gap:1.5rem">
      <div>
        <div class="page-header" style="margin-bottom:.75rem">
          <h2 style="font-size:1rem;font-weight:600">Fachbereiche</h2>
          <button class="btn btn-primary btn-sm" id="btn-add-dept">+ Neuer Fachbereich</button>
        </div>
        <div class="card table-wrap">
          <table><thead><tr>
            <th style="width:30px">Farbe</th><th>Name</th><th>Kurz</th><th style="width:80px;text-align:center">Pflichtst.</th><th style="width:60px;text-align:center">Sort.</th><th style="width:160px">Aktionen</th>
          </tr></thead><tbody id="dept-tbody"></tbody></table>
        </div>
      </div>

      <div>
        <div class="page-header" style="margin-bottom:.75rem">
          <h2 style="font-size:1rem;font-weight:600">Fachbereich-Gruppen</h2>
          <button class="btn btn-primary btn-sm" id="btn-add-group">+ Neue Gruppe</button>
        </div>
        <div class="card table-wrap">
          <table><thead><tr>
            <th>Name</th><th>Fachbereiche</th><th style="width:80px;text-align:center">Öffentlich</th><th style="width:100px;text-align:center">Pool</th><th style="width:60px;text-align:center">Schulen</th><th style="width:160px">Aktionen</th>
          </tr></thead><tbody id="group-tbody"></tbody></table>
        </div>
      </div>
    </div>`;

  el.querySelector("#btn-add-dept")!.addEventListener("click", () => openDeptForm());
  el.querySelector("#btn-add-group")!.addEventListener("click", () => openGroupForm());

  await Promise.all([loadDepartments(), loadGroups()]);
  renderDepartmentsTable(el);
  renderGroupsTable(el);
}
